/**
 * SearchBridge — warstwa przejściowa (Most danych).
 * Jedyna odpowiedzialność: pobrać surowe dane z SearchApi
 * i znormalizować je do formatu gotowego dla maszyny animacji.
 * Nie wie nic o animacjach, DOM ani stanie React.
 */

import { fetchSearch, type SearchApiOptions, type RawSearchItem } from './SearchApi';

export interface NormalizedSearchItem {
  conversationId: number;
  employeeId: number | null;
  firstName: string;
  lastName: string;
  position: string;
  shortSummary: string;
  longSummary: string;
  arrangementsJson?: any;
  createdAt: string;
  similarityScore?: number;
}

export interface NormalizedSearchMeta {
  totalFound: number;
  searchTimeMs?: number;
  parsedDateStart?: string | null;
  parsedDateEnd?: string | null;
  searchConcept?: string | null;
  fallback?: boolean;
}

export interface NormalizedSearchResult {
  items: NormalizedSearchItem[];
  ragAnswer: string | null;
  searchMeta: NormalizedSearchMeta;
}

/** Normalizuje jeden surowy item (snake_case → camelCase, brakujące pola → wartości domyślne). */
function normalizeItem(raw: RawSearchItem): NormalizedSearchItem {
  return {
    conversationId: raw.conversation_id ?? raw.conversationId ?? 0,
    employeeId:     raw.employee_id     ?? raw.employeeId     ?? null,
    firstName:      raw.first_name      ?? raw.firstName      ?? '',
    lastName:       raw.last_name       ?? raw.lastName       ?? '',
    position:       raw.position        ?? '',
    shortSummary:   raw.short_summary   ?? raw.shortSummary   ?? '',
    longSummary:    raw.long_summary    ?? raw.longSummary    ?? '',
    arrangementsJson: raw.arrangements_json,
    createdAt:      raw.created_at      ?? raw.createdAt      ?? '',
    similarityScore: raw._meta?.similarityScore,
  };
}

/**
 * Pobiera wyniki wyszukiwania RAG i normalizuje je.
 * Rzuca Error("Bridge: Brak danych lub uszkodzony format") gdy:
 * - odpowiedź nie zawiera pola `items`
 * - `items` nie jest tablicą
 */
export async function getNormalizedResults(
  query: string,
  options?: SearchApiOptions
): Promise<NormalizedSearchResult> {
  const raw = await fetchSearch(query, options);

  if (!raw || !Array.isArray(raw.items)) {
    throw new Error('Bridge: Brak danych lub uszkodzony format');
  }

  const items = raw.items.map(normalizeItem);

  return {
    items,
    ragAnswer: raw.ragAnswer ?? null,
    searchMeta: {
      totalFound:      raw.searchMeta?.totalFound      ?? items.length,
      searchTimeMs:    raw.searchMeta?.searchTimeMs,
      parsedDateStart: raw.searchMeta?.parsedDateStart ?? null,
      parsedDateEnd:   raw.searchMeta?.parsedDateEnd   ?? null,
      searchConcept:   raw.searchMeta?.searchConcept   ?? null,
      fallback:        raw.searchMeta?.fallback         ?? false,
    },
  };
}
